import { useCallback, useMemo, useState } from "react";
import {
  pageIndexForId,
  pageRequiredComplete,
  surveyRequiredComplete,
  visibleQuestionsOnPage,
} from "./surveyUtils";

/**
 * Local survey answers, current page, and required-field validity.
 *
 * @param {object} definition
 * @param {Record<string, unknown>} [initialAnswers]
 * @param {string} [initialPageId]
 * @returns {{
 *   answers: Record<string, unknown>,
 *   setAnswer: (questionId: string, value: unknown) => Record<string, unknown>,
 *   pageIndex: number,
 *   setPageIndex: (index: number) => void,
 *   page: object,
 *   pages: object[],
 *   visibleOnPage: object[],
 *   pageComplete: boolean,
 *   surveyComplete: boolean,
 *   currentPageId: string,
 * }}
 */
export function useSurveyState(definition, initialAnswers = {}, initialPageId = "") {
  const pages = definition?.pages || [];
  const [answers, setAnswers] = useState(() => ({ ...initialAnswers }));
  const [pageIndex, setPageIndex] = useState(() => pageIndexForId(definition, initialPageId));

  const page = pages[pageIndex] || pages[0];
  const visibleOnPage = useMemo(
    () => visibleQuestionsOnPage(page, answers),
    [page, answers]
  );
  const pageComplete = useMemo(
    () => pageRequiredComplete(page, answers),
    [page, answers]
  );
  const surveyComplete = useMemo(
    () => surveyRequiredComplete(definition, answers),
    [definition, answers]
  );

  const setAnswer = useCallback((questionId, value) => {
    let next = {};
    setAnswers((prev) => {
      next = { ...prev, [questionId]: value };
      return next;
    });
    return next;
  }, []);

  return {
    answers,
    setAnswer,
    pageIndex,
    setPageIndex,
    page,
    pages,
    visibleOnPage,
    pageComplete,
    surveyComplete,
    currentPageId: page?.id || "",
  };
}
